import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Card, Tag, Skeleton, Avatar } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import journeyService from "../../../services/journey";
import { capitalizeFirstLetter } from "../../../utils/utils";
import { CustomButton } from "../physician_page/styled";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useHistory } from "react-router-dom";
import {
  EmptyStateCard,
  EmptyStateText,
  ProgressContainer,
  ProgressText,
  StyledProgress,
  DateInfo,
  DateItem,
  DateSeparator,
} from "./components/Styled";

export const PatientCardWrapper = styled(Card)`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background-color: var(--primary-bg);
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    box-shadow: 0 4px 12px rgba(138, 62, 244, 0.1);
    border-color: rgba(138, 62, 244, 0.3);
  }

  .ant-card-body {
    padding: 10px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .complete-tag {
    position: absolute;
    top: -10px;
    font-size: 11px;
    font-weight: 600;
    left: 14px;
    background-color: #f0fdf4;
    color: #16a34a;
    padding: 0px 12px;
    border: 1px solid #bbf7d0;
    border-radius: 30px;
  }
`;

const PatientHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--border-color);
  padding: 10px;
  border-radius: 12px;
  margin-bottom: 5px;
  width: 100%;
`;

const PatientIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PatientName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--primary-text-color);
  line-height: 1.4;
`;

const PatientEmail = styled.div`
  font-size: 13px;
  color: var(--secondary-text-color);
`;

const PatientContant = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--primary-bg);
  padding: 10px;
  border-radius: 12px;
`;

const PatientActions = styled.div`
  display: flex;
  //   justify-content: flex-end;
  //   margin-top: 16px;
`;

const PageWrapper = styled.div`
  padding: 32px 150px 64px;
  // background: #f8fafc;
  min-height: 100vh;
  text-align: center;
  width: 100%;

  @media (max-width: 1340px) {
    padding: 32px 100px 64px;
  }

  @media (max-width: 1240px) {
    padding: 32px 70px 64px;
  }

  @media (max-width: 1025px) {
    padding: 25px 32px 32px;
  }

  @media (max-width: 480px) {
    padding: 12px 8px 20px;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    padding: 5px 10px;
    color: var(--primary-text-color);
    cursor: pointer;
    width: fit-content;
    border-radius: 6px;
    transition: all 0.3s;
    margin-bottom: 12px;

    @media (max-width: 480px) {
      font-size: 13px;
      padding: 6px 10px;
      margin-bottom: 8px;
    }

    &:hover {
      background-color: var(--border-color);
    }
  }

  .welcome-badge-container {
    @media (max-width: 480px) {
      display: flex;
      justify-content: center;
      margin-bottom: 8px;
    }
  }

  .welcome-badge {
    @media (max-width: 480px) {
      font-size: 11px !important;
      padding: 5px 10px !important;
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      line-height: 1.4;
    }
  }
`;

const HeaderTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-text-color);
  margin: 12px 0 5px;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 8px 0 4px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
    margin: 6px 0 4px;
    line-height: 1.3;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 16px;
  color: var(--secondary-text-color);
  margin: 0 0 16px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    font-size: 13px;
    margin: 0 0 14px;
    padding: 0 4px;
    line-height: 1.4;
  }
`;

const InfoRow = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
  gap: 16px;
  color: var(--secondary-text-color);
  font-size: 14px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    font-size: 11px;
    margin-bottom: 16px;
    padding: 0 4px;
  }
`;

const InfoItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MetricBox = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 12px;
  background-color: var(--primary-bg);
  display: flex;
  //   flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  gap: 8px;
  height: 100%;
  background-color: ${(props) => props.color || "var(--primary-bg)"};
  color: ${(props) => (props.color ? "#fff" : "var(--primary-text-color)")};
`;

const MetricIcon = styled.div`
  color: var(--secondary-text-color);
  //   margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => props.color || "var(--primary-text-color)"};
  line-height: 1.2;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${(props) => props.color || "var(--secondary-text-color)"};
  font-weight: 600;
`;

const BillableTag = styled(Tag)`
  background-color: #10b981;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  margin: 4px 0 0 0;
`;

const JourneyDetailPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [journey, setJourney] = useState(null);
  const [journeyDataFromAssignment, setJourneyDataFromAssignment] =
    useState(false);
  const [loading, setLoading] = useState(false);

  const fetchJourney = async () => {
    try {
      setLoading(true);
      const res = await journeyService.getJourney(id);
      if (res?.data) {
        setJourney(res?.data?.journey);
      }
    } catch (error) {
      console.error("Error fetching journey:", error);
      // keep as null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    fetchJourney();
  }, [id]);

  const handlePreviewClick = (item) => {
    const id = item?.id || item?._id;
    if (id) {
      history.push(`/pages/journeys/preview/${id}`);
    }
  };

  const calculateJourneyDurations = (journey) => {
    const steps = journey?.steps || [];

    let totalSeconds = 0;
    let watchedSeconds = 0;
    
    steps.forEach((step) => {
        const duration = step?.video?.duration || 0;
        totalSeconds += duration;

        if (step?.status === "watched") {
            watchedSeconds += duration;
        }
    });

    return { totalSeconds, watchedSeconds };
  };

  const isFullMinute = (seconds = 0) => seconds > 0 && seconds % 60 === 0;

  const formatTime = (seconds = 0) => {
    if (seconds === 0) return "0";
    if (seconds % 60 === 0) return `${seconds / 60}`;
    return `${seconds}`;
  };

  const getTimeLabel = (total = 0) => {
    if (isFullMinute(total)) {
      return "Minutes";
    }
    return "Seconds";
  };

  return (
    <>
      <Row
        className="journey-preview-row"
        style={{
          width: "100%",
          borderRadius: 10,
        }}
      >
        <PageWrapper>
          {!journeyDataFromAssignment && (
            <div className="back-btn" onClick={() => window.history.back()}>
              <FaArrowLeftLong />
              Go back
            </div>
          )}
          {/* <div className="welcome-badge-container">
            <span
              className="welcome-badge"
              style={{
                fontSize: 14,
                backgroundColor: "var(--border-color)",
                border: "1px solid var(--border-cololr)",
                color: "var(--primary-text-color)",
                padding: "8px 16px",
                borderRadius: 22,
              }}
            >
              <UserOutlined style={{ marginRight: "5px" }} />
              Welcome back
            </span>
          </div> */}

          <HeaderTitle>{journey?.name || "Journey Title"}</HeaderTitle>
          <HeaderSubtitle>
            {capitalizeFirstLetter(
              journey?.description ||
                "Comprehensive plan description goes here."
            )}
          </HeaderSubtitle>

          <InfoRow>
            <InfoItem>
              <span role="img" aria-label="doctor">
                👤
              </span>
              {`Dr.${" "}${journey?.coordinatorId?.name || journey?.physicianId?.name}` ||
                "Dr. Michael Johnson"}
            </InfoItem>
            <InfoItem>
              <ClockCircleOutlined />
              {journey?.durationText || "Ongoing"}
            </InfoItem>
            <InfoItem>
              <span role="img" aria-label="specialty">
                🩺
              </span>
              {journey?.specialty ||
                journey?.steps?.[0]?.video?.specialty ||
                "Endocrinology"}
            </InfoItem>
          </InfoRow>

          <h3
            className="learning-path-title"
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: "var(--primary-text-color)",
              marginBottom: 16,
              textAlign: "left",
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            Patient Details
          </h3>
          <Row gutter={[16, 16]}>
            {journey?.assignedPatients.length === 0 ? (
              <Row gutter={[16, 16]} style={{ width: "100%" }}>
                <Col span={24}>
                  <EmptyStateCard>
                    <EmptyStateText>
                      No patient details available
                    </EmptyStateText>
                  </EmptyStateCard>
                </Col>
              </Row>
            ) : loading ? (
              <Skeleton />
            ) : (
              journey?.assignedPatients.map((item, index) => {
                const progressData =
                  item?.steps.length > 0
                    ? Math.round(
                        (item?.watchedCount / item?.steps.length) * 100
                      )
                    : 0;
                const { totalSeconds, watchedSeconds } = calculateJourneyDurations(item);

                return (
                  <Col
                    lg={8}
                    md={12}
                    sm={24}
                    xs={24}
                    key={item?.id || item?._id || index}
                  >
                    <PatientCardWrapper key={item?.id || item?._id || index}>
                      <PatientHeader>
                        <PatientIdentity style={{ maxWidth: "75%" }}>
                          <Avatar
                            size={40}
                            style={{
                              backgroundColor: "#8a3ef4",
                              color: "#fff",
                              fontWeight: 700,
                            }}
                            icon={
                              item?.patientId?.name ? null : <UserOutlined />
                            }
                          >
                            {item?.patientId?.name
                              ? item.patientId.name.charAt(0).toUpperCase()
                              : null}
                          </Avatar>
                          <div
                            className="text-left"
                            style={{ maxWidth: "75%" }}
                          >
                            <PatientName className="ellipsis">
                              {item?.patientId?.name || "Patient"}
                            </PatientName>
                            <PatientEmail className="ellipsis">
                              {item?.patientId?.email || "-"}
                            </PatientEmail>
                          </div>
                        </PatientIdentity>
                        <div>
                          <Tag
                            color={item?.isCompleted ? "#16a34a" : "purple"}
                            style={{ borderRadius: 16 }}
                          >
                            {item?.isCompleted
                              ? "Completed"
                              : item?.patientId?.status
                              ? "Active"
                              : "Inactive"}
                          </Tag>
                        </div>
                      </PatientHeader>
                      <Row gutter={[12, 12]} style={{ marginTop: 5 }}>
                        <Col span={24}>
                          <MetricBox
                            color="#eff6ff"
                            style={{ border: "1px solid #dbeafe" }}
                          >
                            <div>
                              <MetricIcon>
                                <ClockCircleOutlined
                                  style={{ color: "#1d4ed8" }}
                                  size={14}
                                />
                                <MetricLabel color="#1d4ed8">
                                  Minutes
                                </MetricLabel>
                              </MetricIcon>
                              {/* <BillableTag>
                                {journey?.billableMinutes > journey?.minutes
                                  ? "Billable"
                                  : "In Progress"}
                              </BillableTag> */}
                            </div>
                            <MetricValue color="#1e3a8a">
                              {Math.ceil(item?.watchVideoMinutes / 60) || 0}/
                              {item?.totalVideoMinutes || 0}
                            </MetricValue>
                          </MetricBox>
                        </Col>
                      </Row>
                      <PatientContant>
                        <ProgressContainer className="w-100">
                          <ProgressText>
                            <span>Progress</span>
                            <span className="font-semibold">
                              {item?.watchedCount || 0}/{item?.totalCount || 0}{" "}
                              steps ({progressData || 0}%)
                            </span>
                          </ProgressText>
                          <StyledProgress
                            percent={progressData}
                            showInfo={false}
                            strokeColor="#8a3ef4"
                          />
                        </ProgressContainer>
                      </PatientContant>
                      <DateInfo>
                        <DateItem>
                          <CalendarOutlined
                            style={{
                              fontSize: "14px",
                              color: "var(--secondary-text-color)",
                            }}
                          />
                          <span>
                            Enrolled{" "}
                            {item?.createdAt
                              ? new Date(item?.createdAt).toLocaleDateString()
                              : ""}
                          </span>
                        </DateItem>
                        <DateSeparator>•</DateSeparator>
                        <DateItem>
                          <span>
                            Last contact{" "}
                            {item?.updatedAt
                              ? new Date(item.updatedAt).toLocaleDateString()
                              : ""}
                          </span>
                        </DateItem>
                      </DateInfo>
                      <PatientActions>
                        <CustomButton
                          className="w-100"
                          onClick={() => handlePreviewClick(item)}
                        >
                          Preview
                        </CustomButton>
                      </PatientActions>
                    </PatientCardWrapper>
                  </Col>
                );
              })
            )}
          </Row>
        </PageWrapper>
      </Row>
    </>
  );
};

export default JourneyDetailPage;
